import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import {
  Box,
  Button,
  Container,
  Grid,
  Header,
  Textarea,
  Link,
  SpaceBetween,
  Spinner,
  Alert,
} from '@awsui/components-react';

import PropTypes from 'prop-types';
import { API, Logger } from 'aws-amplify';
import { CALLS_PATH } from '../../routes/constants';
import useCallsContext from '../../contexts/calls';
import queryMeetings from '../../graphql/queries/queryMeetings';
import { formatSummaries } from '../common/summary';
import { CopyButton } from './CopyButton';
import { exportToTextFile } from '../common/download-func';
import PromptModal from '../common';

const logger = new Logger('queryKnowledgeBase');

export const STATUS = {
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

export const ACTION = {
  SUMMARIZE: 'SUMMARIZE',
  QUERY: 'QUERY',
  ANECDOTES: 'ANECDOTES',
};

const ValueWithLabel = ({ label, index, children, type }) => {
  let formattedLabel = '';

  if (type === ACTION.QUERY) {
    formattedLabel = `**Q: ${label}**`;
  } else if (type === ACTION.SUMMARIZE) {
    formattedLabel = `**Action: ${label}**`;
  } else {
    formattedLabel = `**${label}**`;
  }

  return (
    <>
      <Box variant="awsui-key-label">
        <span tabIndex={index}>
          <ReactMarkdown>{label ? formattedLabel : ''}</ReactMarkdown>
        </span>
      </Box>
      {children}
    </>
  );
};

ValueWithLabel.propTypes = {
  label: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
  type: PropTypes.string,
};

ValueWithLabel.defaultProps = {
  type: ACTION.QUERY,
};

const CustomLink = ({ href, children }) => {
  const handleClick = (e) => {
    e.preventDefault();
    // Handle the link click here
    console.log('Link clicked:', href);
    // You can add your custom navigation logic here
  };

  return (
    <Link href={`#${CALLS_PATH}/${href}`} onClick={handleClick}>
      {children}
    </Link>
  );
};
CustomLink.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const RESPONSE_IN_PROGRESS = 'Generating your response ...';

export const MeetingsQueryLayout = () => {
  const { selectedItems, chatHistory, setChatHistory } = useCallsContext();

  const [inputQuery, setInputQuery] = useState('');
  const [meetingQueryStatus, setMeetingQueryStatus] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [summarizeCallStatus, setSummarizeCallStatus] = useState(STATUS.SUCCESS);
  const [summaryErrorMessage, setSummaryErrorMessage] = useState('');
  const [promptModalVisible, setPromptModalVisible] = useState(false);
  const [meetingSummaries, setMeetingSummaries] = useState('');
  const [meetingQueries, setMeetingQueries] = useState([]);

  const getElementByIdAsync = (id) =>
    // eslint-disable-next-line
    new Promise((resolve) => {
      const getElement = () => {
        const element = document.getElementById(id);
        if (element) {
          resolve(element);
        } else {
          requestAnimationFrame(getElement);
        }
      };
      getElement();
    });

  const scrollToBottomOfChat = async () => {
    const chatDiv = await getElementByIdAsync('chatDiv');
    chatDiv.scrollTop = chatDiv.scrollHeight + 200;
  };

  const performQueryMeetings = async (action, input = null, prompt = null) => {
    const callIds = selectedItems.map((item) => item.callId);
    const response = await API.graphql({
      query: queryMeetings,
      variables: {
        callIds,
        action,
        input,
        prompt,
        sessionId,
      },
    });
    return response;
  };

  const updateQueries = (queries, answer) => {
    return queries.map((q) => {
      // If entry has no value property or value is '...', update it
      if (!q.value || q.value === '...') {
        return {
          label: q.label,
          value: answer,
        };
      }
      return q;
    });
  };

  const submitQuery = (query) => {
    if (meetingQueryStatus === true) {
      return;
    }

    setMeetingQueryStatus(true);

    const responseData = {
      label: query,
      value: '...',
      type: ACTION.QUERY,
    };
    const currentQueries = meetingQueries.concat(responseData);
    setMeetingQueries(currentQueries);

    // Add question to chatHistory
    const chatEntry = {
      label: query,
      value: '...',
      type: ACTION.QUERY,
    };
    const updatedChatHistory = [...chatHistory, chatEntry];
    setChatHistory(updatedChatHistory);

    scrollToBottomOfChat();

    logger.debug('Submitting GraphQL query:', query);

    performQueryMeetings(ACTION.QUERY, query, sessionId).then((r) => {
      const queryResponse = JSON.parse(r.data.queryMeetings);

      if (Object.prototype.hasOwnProperty.call(queryResponse, 'systemMessage')) {
        /**
         * when encounter errors
         * {
         *     "data": {
         *         "queryMeetings": {
         *             "systemMessage": "Amazon Bedrock Error: " + error message <-- depending on the exception>
         *             "markdown": "Amazon Bedrock Error: " + error message <-- depending on the exception>
         *         }
         *     }
         * }
         */
        const customerErrorMessage =
          'Sorry, we experienced an issue with your request. ' +
          'If this issue persists, please submit a ticket at ' +
          '<a href="https://t.corp.amazon.com/create/templates/45bd3d02-a843-4260-a5f6-e7e0c2c52e83" target="_blank">ticket queue</a> ' +
          'and our team will assist you as soon as possible.';
        const queries = updateQueries(currentQueries, customerErrorMessage);
        setMeetingQueries(queries);

        // Update chatHistory with error message
        const updatedChat = updateQueries(updatedChatHistory, customerErrorMessage);
        setChatHistory(updatedChat);

        logger.error(
          `Error occurred ${queryResponse.systemMessage} while querying knowledge base`,
          queryResponse.systemMessage,
        );
      } else {
        const queryResponseAnswer = queryResponse.markdown || queryResponse;
        setSessionId(queryResponse.sessionId);
        const queries = updateQueries(currentQueries, queryResponseAnswer);
        setMeetingQueries(queries);

        // Update chatHistory with answer
        const updatedChat = updateQueries(updatedChatHistory, queryResponseAnswer);
        setChatHistory(updatedChat);
      }
    });
    scrollToBottomOfChat();
    setMeetingQueryStatus(false);
  };

  const onSubmit = (e) => {
    submitQuery(inputQuery);
    setInputQuery('');
    e.preventDefault();
    return true;
  };

  const queryMeetingOnClick = async (action) => {
    setSummarizeCallStatus(STATUS.LOADING);

    // Add loading entry to chatHistory for summarize action
    if (action === ACTION.SUMMARIZE) {
      const actionLabel = 'Summarize';
      // Add gray line as a separate entry
      const grayLinePrefix = "<hr style='border: 0; height: 1px; background-color: #cccccc; margin: 10px 0;' />\n\n";

      // First add the gray line entry (with empty label so it doesn't show a label)
      const grayLineEntry = {
        label: '',
        value: grayLinePrefix,
        type: 'DIVIDER',
      };

      // Then add the actual summarize entry
      const chatEntry = {
        label: actionLabel,
        value: RESPONSE_IN_PROGRESS,
        type: action,
      };

      setChatHistory([...chatHistory, grayLineEntry, chatEntry]);
    }

    let response = null;
    try {
      response = await performQueryMeetings(action, inputQuery);

      // Get the response data
      const responseData = response.data.queryMeetings;
      setMeetingSummaries(responseData);

      // Update chatHistory with the summary response
      if (action === ACTION.SUMMARIZE) {
        let formattedResponse = formatSummaries(responseData);
        // Add gray line at the end
        const grayLineSuffix = "\n\n<hr style='border: 0; height: 1px; background-color: #cccccc; margin: 10px 0;' />";
        formattedResponse += grayLineSuffix;

        // Use a function to update state to ensure we have the latest state
        setChatHistory((prevChatHistory) => {
          const updatedChatHistory = [...prevChatHistory];
          const lastIndex = updatedChatHistory.length - 1;

          if (lastIndex >= 0 && updatedChatHistory[lastIndex].type === action) {
            // Keep the gray line prefix that was added when creating the entry
            updatedChatHistory[lastIndex].value = formattedResponse;
          }
          return updatedChatHistory;
        });

        // Only set success status after chat history is updated
        setTimeout(() => {
          setSummarizeCallStatus(STATUS.SUCCESS);
        }, 0);
      } else {
        setSummarizeCallStatus(STATUS.SUCCESS);
      }
    } catch (error) {
      /**
       * Error object looks something like this
       * {
       *     "data": {
       *         "queryMeetings": null
       *     },
       *     "errors": [
       *         {
       *             "path": [
       *                 "queryMeetings"
       *             ],
       *             "data": null,
       *             "errorType": "Lambda:Unhandled",
       *             "errorInfo": null,
       *             "locations": [
       *                 {
       *                     "line": 2,
       *                     "column": 3,
       *                     "sourceName": null
       *                 }
       *             ],
       *             "message": "error occurred" <-- depending on the exception>
       *         }
       *     ]
       * }
       */
      setSummarizeCallStatus(STATUS.ERROR);
      setSummaryErrorMessage('An error occurred while querying meetings');
      logger.error('Error occurred {error.errors[0].message} while querying meetings', error.errors[0]);

      // Update chatHistory with error message
      if (action === ACTION.SUMMARIZE) {
        // Use a function to update state to ensure we have the latest state
        setChatHistory((prevChatHistory) => {
          const updatedChatHistory = [...prevChatHistory];
          const lastIndex = updatedChatHistory.length - 1;

          if (lastIndex >= 0 && updatedChatHistory[lastIndex].type === action) {
            updatedChatHistory[lastIndex].value = 'An error occurred while querying meetings';
          }
          return updatedChatHistory;
        });
      }

      return null;
    }
    return response;
  };

  let summaryData = '';
  if (summarizeCallStatus === STATUS.LOADING) {
    summaryData = RESPONSE_IN_PROGRESS;
  } else {
    summaryData = formatSummaries(meetingSummaries);
  }

  if (summarizeCallStatus === STATUS.ERROR) {
    return <Alert type="error">{summaryErrorMessage}</Alert>;
  }
  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                onClick={() => {
                  queryMeetingOnClick(ACTION.SUMMARIZE);
                }}
              >
                Summarize
              </Button>
              <Button
                onClick={() => {
                  setPromptModalVisible(true);
                }}
              >
                View Prompts
              </Button>

              <CopyButton data={summaryData} disabled={summarizeCallStatus === STATUS.LOADING} />
              <Button
                iconName="download"
                disabled={summarizeCallStatus === STATUS.LOADING}
                onClick={() => {
                  exportToTextFile(summaryData, 'CombinedMeetingSummary')
                    .then(() => {
                      // Handle successful export if needed
                      logger.debug('File exported successfully');
                    })
                    .catch((error) => {
                      // Handle any errors
                      logger.error('Error exporting multi meeting summary:', error);
                    });
                }}
              />
            </SpaceBetween>
          }
        >
          Meetings Query Tool
        </Header>
      }
      footer={
        <form onSubmit={onSubmit}>
          <Grid gridDefinition={[{ colspan: { default: 12, xxs: 9 } }, { default: 12, xxs: 3 }]}>
            <Textarea
              placeholder="Please enter query input"
              onChange={({ detail }) => setInputQuery(detail.value)}
              value={inputQuery}
            />
            <Button type="submit" disabled={summarizeCallStatus === STATUS.LOADING || !inputQuery.trim()}>
              Submit
            </Button>
          </Grid>
        </form>
      }
    >
      <PromptModal visible={promptModalVisible} setVisible={setPromptModalVisible} />

      <div id="chatDiv" style={{ overflow: 'hidden', overflowY: 'auto', height: '200px' }}>
        <SpaceBetween size="s" direction="vertical">
          <SpaceBetween size="m">
            {chatHistory.length > 0
              ? chatHistory.map((entry, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <ValueWithLabel key={i} index={i} label={entry.label} type={entry.type}>
                    {entry.value === '...' || entry.value === RESPONSE_IN_PROGRESS ? (
                      <div style={{ height: '30px' }}>
                        <Spinner />
                      </div>
                    ) : (
                      <ReactMarkdown
                        rehypePlugins={[rehypeRaw]}
                        components={{
                          callid: CustomLink,
                        }}
                      >
                        {entry.value}
                      </ReactMarkdown>
                    )}
                  </ValueWithLabel>
                ))
              : ''}
          </SpaceBetween>
        </SpaceBetween>
      </div>
    </Container>
  );
};

export default MeetingsQueryLayout;
